/**
 * Full-bleed hero layout axis — dish/photo focus vs intro copy side.
 *
 * Owners shoot plating left *or* right. `dishSide` keeps the dish as the
 * visual focus and places the intro on the opposite side with a local pocket veil.
 */
export const HERO_DISH_SIDE = {
  /** Dish / focal subject toward inline-start (left in LTR); copy on end. */
  start: 'start',
  /** Dish / focal subject toward inline-end (right in LTR); copy on start. Default. */
  end: 'end',
} as const;

export const HERO_DISH_SIDE_VALUES = [HERO_DISH_SIDE.start, HERO_DISH_SIDE.end] as const;

export type HeroDishSide = (typeof HERO_DISH_SIDE_VALUES)[number];

export function isHeroDishSide(value: string): value is HeroDishSide {
  return (HERO_DISH_SIDE_VALUES as readonly string[]).includes(value);
}

/** Default fine-dining bias: dish right, story left. */
export const DEFAULT_HERO_DISH_SIDE: HeroDishSide = HERO_DISH_SIDE.end;

/** Reads optional `dishSide` from block content (same value across locales). */
export function readHeroDishSideBlockOverride(
  content: Record<string, Record<string, string>>,
): string | undefined {
  const field = content.dishSide;
  if (!field) {
    return undefined;
  }
  return field.de ?? field.en ?? field.vi ?? Object.values(field)[0];
}

export function resolveHeroDishSide(
  content?: Record<string, Record<string, string>> | null,
): HeroDishSide {
  const raw = content ? readHeroDishSideBlockOverride(content) : undefined;
  if (raw && isHeroDishSide(raw)) {
    return raw;
  }
  return DEFAULT_HERO_DISH_SIDE;
}

/** Intro copy sits opposite the dish. */
export function resolveHeroCopySide(dishSide: HeroDishSide): HeroDishSide {
  return dishSide === HERO_DISH_SIDE.start ? HERO_DISH_SIDE.end : HERO_DISH_SIDE.start;
}
